import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../lib/authStore';
import { api } from '../../lib/api';
import { User, Settings, CheckCircle2, AlertCircle, Save } from 'lucide-react';

export const ClientSettingsPage: React.FC = () => {
  const { user, clientProfile, updateUser } = useAuth();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      organization: user?.organization || '',
      country: user?.country || '',
      companyName: clientProfile?.companyName || '',
      billingAddress: clientProfile?.billingAddress || '',
    },
  });

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      const res = await api.put('/auth/profile', data);
      if (res.data?.success) {
        updateUser(res.data.data.user, res.data.data.clientProfile);
        setSuccessMessage('Profile and account details updated successfully.');
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error?.message || 'Failed to update profile.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
          Account &amp; Author Settings
        </h1>
        <p className="text-xs sm:text-sm text-slate-500">
          Manage your author profile, contact information, and billing details.
        </p>
      </div>

      {successMessage && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
        <h2 className="font-serif text-xl font-bold text-slate-900 border-b border-slate-100 pb-3">
          Author Contact Information
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
              First Name
            </label>
            <input
              type="text"
              {...register('firstName')}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
              Last Name
            </label>
            <input
              type="text"
              {...register('lastName')}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
              Email Address (Account Identifier)
            </label>
            <input
              type="email"
              disabled
              value={user?.email || ''}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed font-mono"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              {...register('phone')}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
              Publishing Imprint / Company Name
            </label>
            <input
              type="text"
              {...register('companyName')}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
              Country
            </label>
            <input
              type="text"
              {...register('country')}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
            />
          </div>
        </div>

        <div className="text-xs">
          <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
            Billing / Remittance Address
          </label>
          <textarea
            rows={3}
            {...register('billingAddress')}
            placeholder="Address for contractual invoices and statements..."
            className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
          />
        </div>

        <div className="pt-4 border-t border-slate-100 flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs shadow-sm transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{isSubmitting ? 'Saving Changes...' : 'Save Profile Changes'}</span>
          </button>
        </div>
      </form>

    </div>
  );
};
